import type { DateTime } from 'luxon'

/**
 * Available quantile options for forecast-based pricing.
 * Maps to forecast data fields:
 * - p10: forecast-lo-80 (10th percentile, lower bound of 80% CI)
 * - p25: forecast-lo-50 (25th percentile, lower bound of 50% CI)
 * - mean: mean value
 * - p75: forecast-hi-50 (75th percentile, upper bound of 50% CI)
 * - p90: forecast-hi-80 (90th percentile, upper bound of 80% CI)
 */
export type PriceQuantile = 'p10' | 'p25' | 'mean' | 'p75' | 'p90'

/**
 * Quantile prices for a single hour.
 * All values are in EUR/MW/h.
 */
export type HourlyQuantilePrices = {
  hour: number // 0-23 in market timezone
  p10: number
  p25: number
  mean: number
  p75: number
  p90: number
}

/**
 * Processed forecast data for a single day.
 * Contains hourly quantile prices keyed by hour (0-23).
 */
export type DailyForecastPrices = {
  date: string // ISO date string (YYYY-MM-DD)
  marketProgramName: string
  hourlyPrices: Map<number, HourlyQuantilePrices>
  /** Number of hours in the day (23, 24, or 25 for DST) */
  dayHours: number
}

/**
 * Configuration for selecting which quantile to use for pricing.
 * Allows different quantiles for UP and DOWN regulation.
 */
export type ForecastPriceSelection = {
  upQuantile: PriceQuantile
  downQuantile: PriceQuantile
}

/**
 * Per-level forecast price configuration.
 * Each bid level can have its own quantile selection.
 */
export type LevelForecastConfig = {
  levelId: string
  selection: ForecastPriceSelection
}

/**
 * Processed forecast row with market timezone DateTime.
 */
export type ProcessedForecastRow = {
  marketDateTime: DateTime
  hour: number // 0-23 in market timezone
  date: string // ISO date string (YYYY-MM-DD)
  p10: number
  p25: number
  mean: number
  p75: number
  p90: number
}

/**
 * Market pairs available for forecast-based pricing.
 * Each market pair represents both UP and DOWN directions.
 * The system will automatically use the UP forecast for UP bids
 * and DOWN forecast for DOWN bids.
 */
export type ForecastMarketPair = 'mfrr' | 'fcr-d' | 'fcr-n' | 'opportunity-cost'

/**
 * Pricing mode for a bid level.
 */
export type PricingMode = 'fixed' | 'forecast'

/**
 * Configuration for a single bid level's pricing strategy.
 * When mode is 'forecast', the market and quantile determine hourly prices.
 * When mode is 'fixed', prices are entered manually in the bid ladder.
 */
export type LevelPricingConfig = {
  levelId: string
  mode: PricingMode
  /** Market pair for forecast pricing (only used when mode is 'forecast') */
  market: ForecastMarketPair
  /** Quantile for forecast pricing (only used when mode is 'forecast') */
  quantile: PriceQuantile
}

/**
 * Complete pricing strategy configuration for all levels.
 */
export type PricingStrategyConfig = {
  levels: LevelPricingConfig[]
}

/**
 * Quantile options with display labels for UI.
 */
export const QUANTILE_OPTIONS: Array<{ value: PriceQuantile; label: string; description: string }> = [
  { value: 'p10', label: 'P10', description: 'Very conservative (low price)' },
  { value: 'p25', label: 'P25', description: 'Moderately conservative' },
  { value: 'mean', label: 'Mean', description: 'Balanced (average)' },
  { value: 'p75', label: 'P75', description: 'Conservative' },
  { value: 'p90', label: 'P90', description: 'Very aggressive (high price)' },
]

/**
 * Market pair options with display labels for UI.
 * Each option represents a market pair (both UP and DOWN directions).
 * Note: opportunity-cost is excluded because it's a single calculated value, not a forecast with quantiles.
 */
export const MARKET_PAIR_OPTIONS: Array<{ value: ForecastMarketPair; label: string; shortLabel: string }> = [
  { value: 'mfrr', label: 'mFRR', shortLabel: 'mFRR' },
  { value: 'fcr-d', label: 'FCR-D', shortLabel: 'FCR-D' },
  { value: 'fcr-n', label: 'FCR-N', shortLabel: 'FCR-N' },
]

/**
 * Quantile ordering for constraint validation.
 * Lower index = more conservative (lower percentile).
 */
export const QUANTILE_ORDER: PriceQuantile[] = ['p10', 'p25', 'mean', 'p75', 'p90']
